// File: src/components/ConstructionDetails.js
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import constructionDetailsData from '../data/constructionDetailsData'
import {
    FiShield,
    FiAward,
    FiThermometer,
    FiWifi,
    FiLock,
    FiSun
} from 'react-icons/fi';

const constructionCategories = [
    {
        id: 'materials',
        name: 'Building Materials',
        icon: <FiShield />,
    },
    {
        id: 'insulation',
        name: 'Insulation',
        icon: <FiThermometer />,
    },
    {
        id: 'technology',
        name: 'Smart Technology',
        icon: <FiWifi />,
    },
    {
        id: 'security',
        name: 'Security Features',
        icon: <FiLock />,
    },
    {
        id: 'sustainability',
        name: 'Sustainability',
        icon: <FiSun />,
    },
    {
        id: 'quality',
        name: 'Quality Standards',
        icon: <FiAward />,
    },
];
const ConstructionDetails = () => {
    const [activeCategory, setActiveCategory] = useState('materials');
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const selectedCategory = constructionDetailsData[activeCategory];

    return (
        <section id="construction" className="py-20 bg-gray-50">
            <div className="container mx-auto px-4">
                <motion.div
                    ref={ref}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-4xl font-bold text-arcdom-navy mb-4">Construction Excellence</h2>
                    <div className="w-20 h-1 bg-arcdom-gold mx-auto mb-6"></div>
                    <p className="max-w-2xl mx-auto text-gray-600">
                        Discover the attention to detail and quality standards that set our constructions apart.
                        Every element is carefully considered to create buildings that stand the test of time.
                    </p>
                </motion.div>

                {/* Category Tabs */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
                    {constructionCategories.map((category) => (
                        <button
                            key={category.id}
                            onClick={() => setActiveCategory(category.id)}
                            className={`flex flex-col items-center justify-center p-4 rounded-lg transition-all duration-300 ${
                                activeCategory === category.id
                                    ? 'bg-arcdom-gold text-white'
                                    : 'bg-white text-arcdom-navy hover:bg-gray-100'
                            }`}
                        >
                            <div className="text-2xl mb-2">{category.icon}</div>
                            <span className="text-sm font-medium text-center">{category.name}</span>
                        </button>
                    ))}
                </div>

                {/* Details Content */}
                <motion.div
                    key={activeCategory}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.4 }}
                    className="bg-white rounded-xl shadow-lg overflow-hidden"
                >
                    <div className="grid grid-cols-1 lg:grid-cols-2">
                        <div className="order-2 lg:order-1 p-8">
                            <h3 className="text-2xl font-semibold text-arcdom-navy mb-4">
                                {selectedCategory.title}
                            </h3>
                            <p className="text-gray-600 mb-6">{selectedCategory.description}</p>

                            <ul className="space-y-3">
                                {selectedCategory.items.map((item, index) => (
                                    <motion.li
                                        key={index}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.3, delay: index * 0.1 }}
                                        className="flex items-start text-gray-600"
                                    >
                                        <svg className="w-5 h-5 text-arcdom-gold mr-2 mt-1 flex-shrink-0" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M5 13L9 17L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                        <span>{item}</span>
                                    </motion.li>
                                ))}
                            </ul>
                        </div>
                        <div className="order-1 lg:order-2 h-72 lg:h-auto">
                            <img
                                src={selectedCategory.image}
                                alt={selectedCategory.title}
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </motion.div>

                {/* Call to Action */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="text-center mt-12"
                >
                    <a
                        href="#contact"
                        className="inline-flex items-center px-6 py-3 bg-arcdom-navy text-white rounded-md font-medium hover:bg-opacity-90 transition-all duration-300"
                    >
                        Request Construction Specifications
                        <svg className="w-4 h-4 ml-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </a>
                </motion.div>
            </div>
        </section>
    );
};

export default ConstructionDetails;
